import React from 'react';
import PropTypes from 'prop-types';
import withErrorBoundary from '@/utils/errorBoundary';
import shieldPath from '../../media/shield.svg';
import monarkShieldPath from '../../media/monarxShield.svg';
import * as Styles from './ProtectionCard.styles';

export const PROTECTION_CARD_TYPES = Object.freeze({
  TRIAL_IS_ACTIVE: 'trialIsActive',
  AUTOMATIC_PROTECTION_AVAILABLE: 'automaticProtectionAvailable',
  SAFE_WITHOUT_INTERRUPTION: 'safeWithoutInterruption',
  CHOOSE_HOW_TO_PROCEED: 'chooseHowToProceed',
});

const DEFAULT_TYPE = PROTECTION_CARD_TYPES.SAFE_WITHOUT_INTERRUPTION;

const parseProtectionCardContent = ({ content, type, trialDaysLeft }) => {
  const protectionCardContent = content && content.protectionCard
    ? content.protectionCard
    : null;

  if (!protectionCardContent) {
    return null;
  }

  const selectedCardContent = protectionCardContent[type]
    || protectionCardContent[DEFAULT_TYPE];

  if (!selectedCardContent) {
    return null;
  }

  const parsedDescription = typeof selectedCardContent.description === 'function'
    ? selectedCardContent.description(trialDaysLeft)
    : selectedCardContent.description;

  const parsedActions = (selectedCardContent.actions || []).map(action => ({
    ...action,
    label: typeof action.label === 'function'
      ? action.label(trialDaysLeft)
      : action.label,
  }));

  return {
    ...selectedCardContent,
    description: parsedDescription,
    actions: parsedActions,
  };
};

const ProtectionCard = ({
  testId,
  content,
  type,
  hideButtons,
  onAction,
  trialDaysLeft,
  clientIsAA,
}) => {
  const parsedCardContent = parseProtectionCardContent({
    content,
    type,
    trialDaysLeft,
  });

  if (!parsedCardContent) {
    return null;
  }

  const handleOnAction = actionKey => () => {
    if (onAction) {
      onAction(actionKey);
    }
  };

  const hasMultipleButtons = parsedCardContent.actions.length > 1;

  return (
    <Styles.Card data-testid={testId}>
      <Styles.Content>
        <Styles.ShieldIcon>
          <Styles.ShieldImage src={shieldPath} alt={parsedCardContent.title} />
        </Styles.ShieldIcon>

        <Styles.TextWrapper>
          <Styles.Title>{parsedCardContent.title}</Styles.Title>

          {parsedCardContent.description && (
          <Styles.Description dangerouslySetInnerHTML={{ __html: parsedCardContent.description }} />
          )}

          {!hideButtons && parsedCardContent.actions.length > 0 && (
            <Styles.ButtonsWrapper data-testid={`${testId}__actions`}>
              {parsedCardContent.actions.map(action => (
                <Styles.ActionButton
                  key={action.key}
                  variant={action.variant}
                  hasMultipleButtons={hasMultipleButtons}
                  onClick={handleOnAction(action.key)}
                  data-testid={`${testId}__action-${action.key}`}
                >
                  {typeof action.label === 'function' ? action.label(clientIsAA) : action.label}
                </Styles.ActionButton>
              ))}
            </Styles.ButtonsWrapper>
          )}
        </Styles.TextWrapper>
      </Styles.Content>

      <Styles.MonarxIcon>
        <Styles.MonarxImage src={monarkShieldPath} alt={parsedCardContent.title} />
      </Styles.MonarxIcon>
    </Styles.Card>
  );
};

ProtectionCard.propTypes = {
  testId: PropTypes.string,
  content: PropTypes.shape({
    protectionCard: PropTypes.object,
  }).isRequired,
  type: PropTypes.oneOf(Object.values(PROTECTION_CARD_TYPES)),
  hideButtons: PropTypes.bool,
  onAction: PropTypes.func,
  trialDaysLeft: PropTypes.number,
};

ProtectionCard.defaultProps = {
  testId: 'protection-card',
  type: DEFAULT_TYPE,
  hideButtons: false,
  onAction: null,
  trialDaysLeft: 0,
};

export default withErrorBoundary(ProtectionCard, 'ProtectionCard');
